/**
 * BrandLogo — single component used by every in-app header that shows
 * the brand mark.
 *
 * Resolution order (iter150AZ.43):
 *   1. Admin-uploaded override (``branding.has_overrides`` true) — use
 *      the uploaded PNG keyed by ``variant`` (default ``any-192``).
 *   2. Static brand icon shipped in ``/public/`` (always exists, so the
 *      mobile + installed-PWA + desktop headers all look identical to
 *      the actual home-screen icon).
 *   3. Phosphor ``Database`` fallback — only reached when the static
 *      PNG fails to load (e.g. offline first paint without service-
 *      worker priming).
 *
 * One place to swap the rendered surface (e.g. switch from a flat
 * <img> to a rounded badge) when the brand evolves.
 *
 * Usage
 * -----
 *   <BrandLogo size={22} className="text-blue-700 shrink-0" />
 */
import { useState } from "react";
import { Database } from "@phosphor-icons/react";
import { useBranding } from "@/lib/branding";

const STATIC_FALLBACK = {
  "any-192": "/icon-192.png",
  "any-512": "/icon-512.png",
  "maskable-192": "/icon-maskable-192.png",
  "maskable-512": "/icon-maskable-512.png",
};

export default function BrandLogo({
  size = 22,
  className = "text-blue-700 shrink-0",
  variant = "any-192",
}) {
  const branding = useBranding();
  const [imgFailed, setImgFailed] = useState(false);

  const overrideSrc = branding.has_overrides ? branding.icons?.[variant] : null;
  const staticSrc = branding.icons?.[variant] || STATIC_FALLBACK[variant];
  const src = overrideSrc || staticSrc;

  if (src && !imgFailed) {
    return (
      <img
        src={src}
        alt={branding.title || "Brand logo"}
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: "contain" }}
        className={`${className} rounded-sm`}
        onError={() => setImgFailed(true)}
        data-testid="brand-logo-img"
      />
    );
  }

  return (
    <Database
      size={size}
      weight="bold"
      className={className}
      data-testid="brand-logo-fallback"
    />
  );
}
